import { useCallback, useEffect, useRef, useMemo, useState, startTransition } from 'react';

import { Box, Tabs } from '@mantine/core';
import { useForm } from '@mantine/form';
import { diff } from 'deep-object-diff';
import { useTranslation } from 'react-i18next';

import { CreditService } from 'services';
import { displayClasses } from 'styles';
import { CreditOptionsResponse, CreditRequestForm, CreditResponse } from 'types';
import { AppUtils } from 'utils';

import CreditRequestIncomes from './incomes';
import CreditRequestOutcomes from './outcomes';
import CreditRequestPersonal from './personal';
import CreditRequestRequest from './request';

interface CreditRequestProps {
  credit: CreditResponse;
  options: CreditOptionsResponse;
  readOnly?: boolean;
}

const TABS = {
  CREDIT: 'credit',
  PERSONAL: 'personal',
  INCOMES: 'incomes',
  OUTCOMES: 'outcomes',
};

const CreditRequest = ({ credit, options, readOnly = false }: CreditRequestProps) => {
  const { t } = useTranslation();
  const updatingTimeout = useRef<NodeJS.Timeout>();

  const [visibleFields, setVisibleFields] = useState<string[]>(
    AppUtils.getVisibleRequestAndPersonalFields(credit as CreditRequestForm),
  );
  const valuesToSend = useRef<any>({});
  const isInternalUpdate = useRef<boolean>(false);
  const visibleFieldsRef = useRef<string[]>(visibleFields);
  visibleFieldsRef.current = visibleFields;

  const updateFields = useCallback(async (values: any, previous: any) => {
    if (readOnly || isInternalUpdate.current || Object.keys(previous).length === 0) return;
    isInternalUpdate.current = true;

    const diffValues: any = diff(previous, values);
    console.log('diffValues', diffValues);
    if (Object.keys(diffValues).length === 0) {
      isInternalUpdate.current = false;
      return;
    }

    if (diffValues['creditAmount'] || diffValues['paymentPeriod']) {
      AppUtils.populateMonthlyInstallment(values.creditAmount, values.paymentPeriod, form);
    }

    const prevVisibleFields = visibleFieldsRef.current;
    const newVisibleFields: string[] = AppUtils.getVisibleRequestAndPersonalFields(values);

    if (diffValues['children']) diffValues['children'] = values['children'];
    if (diffValues['employments']) diffValues['employments'] = values['employments'];
    if (diffValues['incomes']) diffValues['incomes'] = values['incomes'];
    if (diffValues['expenses']) diffValues['expenses'] = values['expenses'];

    prevVisibleFields.forEach((el) => {
      if (!newVisibleFields.includes(el) && previous[el] !== undefined) {
        diffValues[el] = null;
      }
    });
    startTransition(() => {
      Object.entries(diffValues).forEach(([key, val]) => {
        form.setFieldValue(key, val, { forceUpdate: false });
      });
      setVisibleFields(newVisibleFields);
    });
    updateCredit(diffValues);
    isInternalUpdate.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {} as CreditRequestForm | any,
    validateInputOnChange: false,
    validate: {
      houseNumber: (value) =>
        value && !/^[0-9]/.test(value.charAt(0)) ? t('user.address.houseNumber.startWith1') : null,
      previousHouseNumber: (value) =>
        value && !/^[0-9]/.test(value.charAt(0)) ? t('user.address.houseNumber.startWith1') : null,
    },
    onValuesChange: (values, previous) => {
      updateFields(values, previous);
    },
  });

  useEffect(() => {
    form.setValues({
      ...credit,
      user: {
        ...credit.user,
        firstName: credit.user.fullName.split(' ')[0],
        lastName: credit.user.fullName.split(' ')[1],
        phone: credit.user.countryCode + ' ' + credit.user.phoneNumber,
      },
      birthDate: credit.birthDate ? new Date(credit.birthDate) : undefined,
      visaFrom: credit.visaFrom ? new Date(credit.visaFrom) : undefined,
      livingFrom: credit.livingFrom ? new Date(credit.livingFrom) : undefined,
      partnerBirthDate: credit.partnerBirthDate ? new Date(credit.partnerBirthDate) : undefined,
      partnerVisaFrom: credit.partnerVisaFrom ? new Date(credit.partnerVisaFrom) : undefined,
      saturdayAvailability: Boolean(
        credit.saturdayAvailabilityFrom || credit.saturdayAvailabilityTo,
      ),
      partnerSaturdayAvailability: Boolean(
        credit.partnerSaturdayAvailabilityFrom || credit.partnerSaturdayAvailabilityTo,
      ),
      additionalExpenses: Boolean(
        credit.expenses?.[0]?.expenseType || credit.expenses?.[0]?.amount,
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCredit = useCallback(async (item: any) => {
    if (Object.keys(item).length === 0) {
      return;
    }
    clearTimeout(updatingTimeout.current);

    const clearedItem = item;
    delete clearedItem.additionalExpenses;

    valuesToSend.current = { ...valuesToSend.current, ...clearedItem };
    updatingTimeout.current = setTimeout(async () => {
      CreditService.creditsUpdateMultipleProperties(credit.id, valuesToSend.current);
      valuesToSend.current = {};
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = useMemo(
    () => [
      {
        value: TABS.CREDIT,
        component: (
          <CreditRequestRequest
            form={form}
            options={options}
            visibleFields={visibleFields}
            readOnly={readOnly}
          />
        ),
      },
      {
        value: TABS.PERSONAL,
        component: (
          <CreditRequestPersonal
            credit={credit}
            form={form}
            options={options}
            visibleFields={visibleFields}
            readOnly={readOnly}
          />
        ),
      },
      {
        value: TABS.INCOMES,
        component: (
          <CreditRequestIncomes credit={credit} form={form} options={options} readOnly={readOnly} />
        ),
      },
      {
        value: TABS.OUTCOMES,
        component: (
          <CreditRequestOutcomes
            credit={credit}
            form={form}
            options={options}
            readOnly={readOnly}
          />
        ),
      },
    ],
    [form, options, credit, visibleFields, readOnly],
  );

  return (
    <Box>
      <Tabs defaultValue={tabs[0].value} variant={'pills'} mt={'lg'}>
        <Tabs.List grow={true}>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={displayClasses.tabView}>
              {t('credit.view.tab.' + tab.value)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={displayClasses.tabContent}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  );
};

export default CreditRequest;
